/**
 * MiniCalendar Component
 * A fully custom styled calendar for date selection.
 * Supports: dark mode, brand colors, range highlighting,
 * today highlight, future-date disabling, and month navigation.
 */
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";

interface MiniCalendarProps {
  /** Currently selected date (YYYY-MM-DD) */
  value: string;
  /** Called with the new date string when a day is selected */
  onChange: (date: string) => void;
  /** Max selectable date (YYYY-MM-DD). Dates after this are disabled. */
  max?: string;
  /** Min selectable date (YYYY-MM-DD). Dates before this are disabled. */
  min?: string;
  /** Range start date for range highlighting (YYYY-MM-DD) */
  rangeStart?: string;
  /** Range end date for range highlighting (YYYY-MM-DD) */
  rangeEnd?: string;
  darkMode: boolean;
}

const DAY_NAMES = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function toDateStr(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export default function MiniCalendar({
  value,
  onChange,
  max,
  min,
  rangeStart,
  rangeEnd,
  darkMode,
}: MiniCalendarProps) {
  const today = new Date().toISOString().split("T")[0]!;

  // Parse initial display month from value or today
  const parseSeed = value || today;
  const seedParts = parseSeed.split("-");
  const seedYear = parseInt(
    seedParts[0] ?? String(new Date().getFullYear()),
    10,
  );
  const seedMonth = parseInt(seedParts[1] ?? "1", 10) - 1;

  const [displayYear, setDisplayYear] = useState(seedYear);
  const [displayMonth, setDisplayMonth] = useState(seedMonth);

  const prevMonth = () => {
    if (displayMonth === 0) {
      setDisplayMonth(11);
      setDisplayYear((y) => y - 1);
    } else {
      setDisplayMonth((m) => m - 1);
    }
  };

  const nextMonth = () => {
    if (displayMonth === 11) {
      setDisplayMonth(0);
      setDisplayYear((y) => y + 1);
    } else {
      setDisplayMonth((m) => m + 1);
    }
  };

  // Disable next if first day of next month > max
  const canGoNext = useMemo(() => {
    if (!max) return true;
    const nextMonthFirst = toDateStr(
      displayMonth === 11 ? displayYear + 1 : displayYear,
      displayMonth === 11 ? 0 : displayMonth + 1,
      1,
    );
    return nextMonthFirst <= max;
  }, [displayYear, displayMonth, max]);

  // Build grid: blanks + days of month
  const daysInMonth = new Date(displayYear, displayMonth + 1, 0).getDate();
  const firstDayOfWeek = new Date(displayYear, displayMonth, 1).getDay();
  const cells: (number | null)[] = [
    ...Array.from<null>({ length: firstDayOfWeek }).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  // Pad to full weeks
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="select-none">
      {/* Month/Year header */}
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={prevMonth}
          className={`p-1 rounded-lg transition-all ${
            darkMode
              ? "text-gray-400 hover:bg-gray-700 hover:text-white"
              : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"
          }`}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span
          className={`text-sm font-semibold ${darkMode ? "text-gray-100" : "text-gray-800"}`}
        >
          {MONTH_NAMES[displayMonth]} {displayYear}
        </span>
        <button
          type="button"
          onClick={nextMonth}
          disabled={!canGoNext}
          className={`p-1 rounded-lg transition-all ${
            !canGoNext
              ? "opacity-30 cursor-not-allowed"
              : darkMode
                ? "text-gray-400 hover:bg-gray-700 hover:text-white"
                : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"
          }`}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Day of week headers */}
      <div className="grid grid-cols-7 mb-1">
        {DAY_NAMES.map((d) => (
          <div
            key={d}
            className={`text-center text-[10px] font-medium pb-1 ${
              darkMode ? "text-gray-500" : "text-gray-400"
            }`}
          >
            {d}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7 gap-y-0.5">
        {cells.map((day, idx) => {
          if (day === null) {
            return <div key={`blank-${idx}`} />;
          }

          const dateStr = toDateStr(displayYear, displayMonth, day);
          const isToday = dateStr === today;
          const isSelected = dateStr === value;
          const isDisabled =
            (max !== undefined && dateStr > max) ||
            (min !== undefined && dateStr < min);

          // Range highlight: between rangeStart and rangeEnd
          const rs = rangeStart ?? "";
          const re = rangeEnd ?? "";
          const isInRange =
            rs && re && rs !== re && dateStr > rs && dateStr < re;
          const isRangeStart = rs && dateStr === rs;
          const isRangeEnd = re && dateStr === re;

          return (
            <button
              key={dateStr}
              type="button"
              disabled={isDisabled}
              onClick={() => !isDisabled && onChange(dateStr)}
              className={[
                "relative flex items-center justify-center h-7 w-full text-xs font-medium transition-all",
                isDisabled ? "opacity-25 cursor-not-allowed" : "cursor-pointer",
                // Range fill (background strip)
                isInRange
                  ? darkMode
                    ? "bg-[var(--brand-primary)]/15"
                    : "bg-[var(--brand-primary)]/10"
                  : "",
                (isRangeStart || isRangeEnd) && !isSelected
                  ? darkMode
                    ? "bg-[var(--brand-primary)]/15"
                    : "bg-[var(--brand-primary)]/10"
                  : "",
                // Selected day circle
                isSelected
                  ? "bg-[var(--brand-primary)] text-white rounded-full shadow-sm shadow-[var(--brand-primary)]/40"
                  : isToday && !isDisabled
                    ? darkMode
                      ? "text-[var(--brand-primary)] font-bold"
                      : "text-[var(--brand-primary)] font-bold"
                    : darkMode
                      ? isDisabled
                        ? "text-gray-600"
                        : "text-gray-300 hover:bg-gray-700 hover:rounded-full"
                      : isDisabled
                        ? "text-gray-300"
                        : "text-gray-700 hover:bg-gray-100 hover:rounded-full",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {/* Today dot */}
              {isToday && !isSelected && (
                <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[var(--brand-primary)]" />
              )}
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}
